import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonWidth,
  compact,
  DateFormat,
  DAY,
  formatDate,
  formatRelativeDate,
  formatTime,
  formatTimeFromMinutes,
  IconChevronLeft,
  IconChevronRight,
  MINUTE,
  Popover,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useMemo, useRef, useState } from 'react';

import { type Scalars } from '../../../../__generated-gql-types__/globalTypes';
import { capitalize } from '../../../../utils/formatters';
import LoadingIndicator from '../../../common/LoadingIndicator';
import { type ScheduleModalStep, type SessionOption } from '../ScheduleModal';

import {
  BaseScheduleModalLayout,
  type BaseScheduleModalLayoutProps,
} from './BaseScheduleModalLayout';
import {
  type CoachCalendarCoachFragment,
  type CoachCalendarCoachingSessionFragment,
  useCoachCalendarAvailabilityQuery,
} from './__generated-gql-types__/CoachCalendar.generated';

const useCoachCalendarAvailability = ({
  coachUrn,
  startTime,
  minRangeInMinutes = 30,
}: {
  coachUrn: string;
  startTime: number;
  minRangeInMinutes?: number;
}) => {
  const { data, loading } = useCoachCalendarAvailabilityQuery({
    variables: {
      coachUrn,
      dateRange: {
        start: startTime,
        end: new Date(startTime).setHours(23, 59, 59, 999) + DAY * 60,
      },
      minRangeInMinutes,
    },
  });
  const blocksByDayMemo = useMemo(() => {
    const blocksByDay: Array<{
      dayEnd: number;
      humanReadable: string; // for debugging
      blocks: Array<{
        start: number;
        end: number;
      }>;
    }> = [];
    if (
      data?.coachCalendarAvailability &&
      data?.coachCalendarAvailability.length > 0
    ) {
      // yes, this is faster than using shift/unshift
      const reverseSortedBlocks = [...data.coachCalendarAvailability].reverse();

      // get end of start day
      const endOfFirstDay = new Date(startTime);
      endOfFirstDay.setHours(23, 59, 59, 999);

      // initialize i to 0
      let indexToInsert = 0;

      while (reverseSortedBlocks.length > 0) {
        // use Date object explicitly to handle weird situations like DST
        const targetDayEnd = new Date(endOfFirstDay).setDate(
          endOfFirstDay.getDate() + indexToInsert,
        );

        if (!blocksByDay[indexToInsert]) {
          blocksByDay[indexToInsert] = {
            dayEnd: targetDayEnd,
            humanReadable: new Date(targetDayEnd).toLocaleDateString(),
            blocks: [],
          };
        }
        const block = reverseSortedBlocks.pop();
        if (!block?.start || !block?.end) continue; // make TS happy
        // for each block, if start is after end, increment i
        if (block.start > targetDayEnd) {
          indexToInsert++;
          reverseSortedBlocks.push(block);
          continue;
        }
        // if end is after end of day, break up day into blocks
        if (block.end > targetDayEnd) {
          blocksByDay[indexToInsert].blocks.push({
            start: block.start,
            end: targetDayEnd,
          });
          reverseSortedBlocks.push({
            start: targetDayEnd + 1,
            end: block.end,
          });
          continue;
        }
        // add blocks to array (fully in bounds)
        blocksByDay[indexToInsert].blocks.push({
          start: block.start,
          end: block.end,
        });
      }
    }
    return blocksByDay;
  }, [data?.coachCalendarAvailability, startTime]);

  return {
    data: blocksByDayMemo,
    loading,
  };
};

interface CoachCalendarProps extends BaseScheduleModalLayoutProps {
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  coach: CoachCalendarCoachFragment;
  isReschedule?: boolean;
  selectedTime: Possible<number>;
  coachingSession?: Possible<CoachCalendarCoachingSessionFragment>;
  hasUnlimitedBookingPower: boolean;
  sessionLength: number;
  sessionType?: SessionOption;
  setOpen: (open: boolean) => void;
  setSelectedTime: (time: Possible<number>) => void;
  setSessionLength: (length: number) => void;
  setSessionType?: (type: SessionOption) => void;
  setStep: (step: ScheduleModalStep) => void;
  sidebarLocked?: boolean;
  onMoveToSubmissionStep: (isComingFromCategorySelection?: boolean) => void;
}

export const CoachCalendar: FC<CoachCalendarProps> = ({
  categoryUrn,
  coach,
  isReschedule,
  selectedTime,
  coachingSession,
  hasUnlimitedBookingPower,
  order,
  sessionLength,
  sessionType,
  setOpen,
  setSelectedTime,
  setSessionLength,
  setSessionType,
  setStep,
  sidebarLocked,
  onMoveToSubmissionStep,
  ...baseScheduleModalProps
}) => {
  const isLg = useMediaQuery(Breakpoints.LG);
  const isMd = useMediaQuery(Breakpoints.MD);

  const [page, setPage] = useState<number>(0);

  const startTime = useRef<number>(new Date().getTime());
  const COACH_CALENDAR_PAGE_SIZE = isLg ? 5 : isMd ? 4 : 3;
  // max span is 5 according to COACH_CALENDAR_PAGE_SIZE
  const colSpanClassNameByCount = {
    0: '',
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
  };

  const { data: blocksByDay, loading } = useCoachCalendarAvailability({
    coachUrn: coach.id,
    startTime: startTime.current,
    minRangeInMinutes: sessionLength,
  });

  interface TimeOfDay {
    time: string;
    value: number;
  }

  interface TimesByDay {
    dayEnd: number;
    humanReadable: string;
    times: TimeOfDay[];
  }

  const timesByDay = useMemo(() => {
    let foundFirstAvailableDay = false;
    return blocksByDay
      ?.map<TimesByDay | null>(({ blocks, dayEnd, humanReadable }) => {
        if (
          coach.availability?.nextAvailableAt &&
          dayEnd < coach.availability.nextAvailableAt
        )
          return null;
        const times: TimeOfDay[] = [];
        blocks.forEach(({ start, end }) => {
          let time = start;
          while (time < end) {
            if (time + sessionLength * MINUTE > end) break;
            times.push({
              time: formatTime(time, { excludeTimeZone: true }),
              value: time,
            });
            time += 30 * MINUTE;
          }
        });
        if (!foundFirstAvailableDay && times.length === 0) {
          return null;
        }
        if (times.length > 0) {
          foundFirstAvailableDay = true;
        }
        return {
          dayEnd,
          humanReadable,
          times,
        };
      })
      .filter(compact);
  }, [blocksByDay, sessionLength, coach.availability?.nextAvailableAt]);

  const daysToDisplay = useMemo(() => {
    return (
      timesByDay?.slice(
        page * COACH_CALENDAR_PAGE_SIZE,
        (page + 1) * COACH_CALENDAR_PAGE_SIZE,
      ) || []
    );
  }, [timesByDay, page, COACH_CALENDAR_PAGE_SIZE]);

  const cyclesToDisplay: Nullable<number[]> = useMemo(() => {
    return coach.coachingTimeSummary?.cycles?.length && daysToDisplay.length
      ? daysToDisplay.reduce<number[]>((counts, day) => {
          const cycleIndex =
            coach.coachingTimeSummary?.cycles?.findIndex(
              (c) => (c.end ?? 0) >= day.dayEnd,
            ) ?? 0;
          counts[cycleIndex] = (counts[cycleIndex] ?? 0) + 1;
          return counts;
        }, [])
      : null;
  }, [daysToDisplay, coach.coachingTimeSummary?.cycles]);

  const isNextDisabled = useMemo(() => {
    return (page + 1) * COACH_CALENDAR_PAGE_SIZE >= timesByDay.length;
  }, [page, COACH_CALENDAR_PAGE_SIZE, timesByDay.length]);

  return (
    <BaseScheduleModalLayout
      buttonProps={{
        onClick: () => onMoveToSubmissionStep(),
        disabled: !selectedTime,
        width: ButtonWidth.FULL_MOBILE,
      }}
      coach={coach}
      coachingSession={coachingSession}
      hasUnlimitedBookingPower={hasUnlimitedBookingPower}
      order={order}
      selectedTime={selectedTime}
      sessionLength={sessionLength}
      setOpen={setOpen}
      setSelectedTime={setSelectedTime}
      setSessionLength={setSessionLength}
      sessionType={sessionType}
      setSessionType={setSessionType}
      isReschedule={isReschedule}
      sidebarLocked={sidebarLocked}
      preventScroll
      showSelectedTime={true}
      {...baseScheduleModalProps}
    >
      <div className="flex max-h-screen flex-col md:max-h-none md:grow md:flex-row md:overflow-hidden">
        <div className="flex grow flex-col">
          <div className="flex items-center p-4">
            <div className="flex grow flex-col text-lg font-medium text-leland-gray-dark sm:flex-row sm:space-x-1">
              <span>{coach.user.firstName}&rsquo;s Calendar</span>
              <span className="hidden font-normal text-leland-gray-light sm:block">
                &middot;
              </span>
              <span className="font-normal text-leland-gray-light">
                All times shown in{' '}
                {
                  new Date()
                    .toLocaleDateString(undefined, {
                      day: '2-digit',
                      timeZoneName: 'short',
                    })
                    .split(', ')[1]
                }
              </span>
            </div>
            <button
              className="disabled:opacity-50"
              disabled={page === 0}
              type="button"
              onClick={() => setPage(page - 1)}
            >
              <IconChevronLeft className="size-5" />
            </button>
            <Popover
              asChild
              content={<p>You can only schedule up to 60 days in advance</p>}
              disablePopover={loading || !isNextDisabled}
            >
              <button
                className="ml-4 disabled:opacity-50"
                type="button"
                disabled={loading || isNextDisabled}
                onClick={() => {
                  setPage(page + 1);
                }}
              >
                <IconChevronRight className="size-5" />
              </button>
            </Popover>
          </div>
          {loading ? (
            <div className="mx-auto flex grow px-16">
              <LoadingIndicator
                className="place-self-center"
                iconClassName="h-28 w-28"
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 border-y border-leland-gray-stroke p-4 md:grid-cols-4 lg:grid-cols-5">
                {daysToDisplay.map(({ dayEnd }) => (
                  <div key={dayEnd} className="flex flex-col items-center">
                    <span className="text-lg font-medium text-leland-gray-dark">
                      {capitalize(
                        formatRelativeDate(dayEnd, Date.now(), {
                          fallback: DateFormat.LONG_DAY_OF_WEEK,
                        }),
                      )}
                    </span>
                    <span className="text-lg text-leland-gray-light">
                      {formatDate(dayEnd, DateFormat.LONG_MONTH_DAY)}
                    </span>
                  </div>
                ))}
              </div>
              {cyclesToDisplay &&
              // show cycles if the first cycle time is less than the session length
              (coach.coachingTimeSummary?.cycles?.at(0)?.timeInMinute ?? 0) <
                sessionLength ? (
                <div className="grid w-full grid-cols-3 gap-2 border-b border-leland-gray-stroke p-2 md:grid-cols-4 lg:grid-cols-5">
                  {cyclesToDisplay.map((count, cycleIndex) => (
                    <div
                      key={`${cycleIndex}::${count}`}
                      className={`w-full rounded bg-leland-gray-hover px-2 py-1 ${colSpanClassNameByCount[count]}`}
                    >
                      <p className="truncate text-sm font-medium text-leland-gray-light">
                        {(coach.coachingTimeSummary?.cycles?.at(cycleIndex)
                          ?.timeInMinute ?? 0) >= sessionLength
                          ? `Schedule using ${cycleIndex === 0 ? 'this' : 'next'} month's balance`
                          : `Buy ${formatTimeFromMinutes(
                              sessionLength -
                                (coach.coachingTimeSummary?.cycles?.at(
                                  cycleIndex,
                                )?.timeInMinute ?? 0),
                            )} to cover this session.`}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="grid shrink grow grid-cols-3 gap-4 p-4 md:grid-cols-4 md:overflow-scroll md:pb-40 lg:grid-cols-5">
                {daysToDisplay.map(({ dayEnd, times }) => (
                  <div key={dayEnd} className="flex flex-col space-y-3">
                    {times?.length ? (
                      times.map(({ time, value }) => {
                        return (
                          <Button
                            key={time}
                            label={time}
                            buttonColor={ButtonColor.LIGHT_BLUE}
                            onClick={() => {
                              setSelectedTime(value);
                            }}
                            selected={selectedTime === value}
                          />
                        );
                      })
                    ) : (
                      <span className="text-center font-medium text-leland-gray-extra-light">
                        Fully booked
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </BaseScheduleModalLayout>
  );
};
